import { Box, Tooltip as MUITooltip } from '@mui/material';
import { getConfirmedKpiRequest } from 'api/requests/kpis-requests';
import { UPInlineMultiSelectDropdown } from 'components/dropdown';
import { UserSourceFilter } from 'constants/UserSourceFilter';
import StatsTile, { StatsTileItem } from 'modules/dashboard/components/stats-tile/StatsTile';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from 'store';
import { setMenuStatusAction } from 'store/actions/menu-actions';
import {
  DASHBOARD__CONFIRMED_KPI,
  DASHBOARD__CONFIRMED_KPI_CURRENT_WEEK,
  DASHBOARD__CONFIRMED_KPI_LAST_MONTH,
  DASHBOARD__CONFIRMED_KPI_LAST_WEEK,
  DASHBOARD__CONFIRMED_KPI_SOURCE_ALL,
  DASHBOARD__CONFIRMED_KPI_SOURCE_SHYFTER,
  DASHBOARD__CONFIRMED_KPI_SOURCE_STROBBO,
  DASHBOARD__CONFIRMED_KPI_SOURCE_SUPERPLANNER,
  DASHBOARD__CONFIRMED_KPI_SOURCE_UPORTAL,
  DASHBOARD__GO_TO_WIZARD,
  DASHBOARD__KPI_FETCH_ERROR,
  MENU__REPORTS,
} from 'translations/constants';
import { IConfirmedKpiPeriod } from 'types/kpis/IConfirmedKpiPeriod';
import { ReactComponent as WarningIcon } from '../../../../assets/icons/ContractErrorIcon.svg';
import { ViewReportsButton } from './styles';

const ALL_VALUE = String(UserSourceFilter.ALL);

const PERIOD_DISPLAY = [
  { key: 'LAST_WEEK', color: '#5F6161' },
  { key: 'LAST_MONTH', color: '#71B790' },
  { key: 'CURRENT_WEEK', color: '#008C15' },
] as const;

const DashboardConfirmedKpiTile = (): JSX.Element => {
  const [t] = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const handleGoToReports = () => {
    dispatch(setMenuStatusAction(t(MENU__REPORTS)));
    navigate('/reports');
  };

  const [selectedSources, setSelectedSources] = useState<string[]>([ALL_VALUE]);
  const [periods, setPeriods] = useState<IConfirmedKpiPeriod[]>([]);
  const [hasError, setHasError] = useState<boolean>(false);

  useEffect(() => {
    let cancelled = false;
    setHasError(false);
    const userSource = selectedSources.map(v => Number(v) as UserSourceFilter);
    getConfirmedKpiRequest({ userSource })
      .then(data => {
        if (!cancelled) setPeriods(data);
      })
      .catch(() => {
        if (cancelled) return;
        setHasError(true);
        setPeriods([]);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedSources]);

  const sourceItems = useMemo(
    () => [
      { value: String(UserSourceFilter.ALL), label: t(DASHBOARD__CONFIRMED_KPI_SOURCE_ALL) },
      { value: String(UserSourceFilter.UPORTAL), label: t(DASHBOARD__CONFIRMED_KPI_SOURCE_UPORTAL) },
      { value: String(UserSourceFilter.STROBBO), label: t(DASHBOARD__CONFIRMED_KPI_SOURCE_STROBBO) },
      { value: String(UserSourceFilter.SUPERPLANNER), label: t(DASHBOARD__CONFIRMED_KPI_SOURCE_SUPERPLANNER) },
      { value: String(UserSourceFilter.SHYFTER), label: t(DASHBOARD__CONFIRMED_KPI_SOURCE_SHYFTER) },
    ],
    [t],
  );

  const periodLabels: Record<string, string> = useMemo(
    () => ({
      LAST_WEEK: t(DASHBOARD__CONFIRMED_KPI_LAST_WEEK),
      CURRENT_WEEK: t(DASHBOARD__CONFIRMED_KPI_CURRENT_WEEK),
      LAST_MONTH: t(DASHBOARD__CONFIRMED_KPI_LAST_MONTH),
    }),
    [t],
  );

  const stats: StatsTileItem[] = useMemo(() => {
    const byPeriod = new Map(periods.map(p => [p.period, p]));
    return PERIOD_DISPLAY.map(({ key, color }) => {
      const entry = byPeriod.get(key);
      const value = hasError || !entry ? '—' : `${entry.percentage}%`;
      return {
        key,
        label: periodLabels[key],
        value,
        labelColor: color,
        valueColor: color,
      };
    });
  }, [periods, hasError, periodLabels]);

  return (
    <StatsTile
      title={t(DASHBOARD__CONFIRMED_KPI)}
      titleAdornment={
        hasError ? (
          <MUITooltip title={t(DASHBOARD__KPI_FETCH_ERROR)} placement="right" arrow>
            <Box display="flex" alignItems="center">
              <WarningIcon width={20} height={20} />
            </Box>
          </MUITooltip>
        ) : undefined
      }
      stats={stats}
      headerBackground="#FFFFFF"
      headerTextColor="#9EA2A2"
      headerSlot={
        <UPInlineMultiSelectDropdown
          items={sourceItems}
          values={selectedSources}
          onChange={setSelectedSources}
          allValue={ALL_VALUE}
          id="confirmed-kpi-source"
          color="#9EA2A2"
        />
      }
      footerSlot={<ViewReportsButton text={t(DASHBOARD__GO_TO_WIZARD)} onClick={handleGoToReports} />}
    />
  );
};

export default DashboardConfirmedKpiTile;
